import type { AdminAgentPanelResize, AdminAgentResizeDrag } from "../models/admin-agent.models";
import { adminAgentPanelWidthAtom } from "../state/admin-agent.atoms";
import { clampAdminAgentPanelWidth, getAdminAgentPanelMaximumWidth } from "../utils/admin-agent-panel-size.utils";
import { ADMIN_AGENT_PANEL_MIN_WIDTH, ADMIN_AGENT_PANEL_RESIZE_STEP } from "../utils/admin-agent.constants";
import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";

// Keep drag feedback in memory and write the browser preference only when a drag ends.
// Pointer capture keeps resizing reliable when the pointer leaves the narrow handle.
export const useAdminAgentPanelResize = (): AdminAgentPanelResize => {
  const [savedWidth, setSavedWidth] = useAtom(adminAgentPanelWidthAtom);
  const [dragWidth, setDragWidth] = useState<number>();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const drag = useRef<AdminAgentResizeDrag>();
  const width = clampAdminAgentPanelWidth(dragWidth ?? savedWidth, viewportWidth);
  const minimumWidth = Math.min(ADMIN_AGENT_PANEL_MIN_WIDTH, viewportWidth);
  const maximumWidth = getAdminAgentPanelMaximumWidth(viewportWidth);

  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", updateViewportWidth);

    return () => window.removeEventListener("resize", updateViewportWidth);
  }, []);

  const onPointerDown: AdminAgentPanelResize["onPointerDown"] = (event) => {
    if (!event.isPrimary || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: width, currentWidth: width };
    setDragWidth(width);
  };

  const onPointerMove: AdminAgentPanelResize["onPointerMove"] = (event) => {
    const current = drag.current;

    if (!current || current.pointerId !== event.pointerId) {
      return;
    }

    current.currentWidth = clampAdminAgentPanelWidth(
      current.startWidth + current.startX - event.clientX,
      viewportWidth,
    );
    setDragWidth(current.currentWidth);
  };

  const finishResize = () => {
    const current = drag.current;

    if (!current) {
      return;
    }

    drag.current = undefined;
    setSavedWidth(clampAdminAgentPanelWidth(current.currentWidth, viewportWidth));
    setDragWidth(undefined);
  };

  const onPointerUp: AdminAgentPanelResize["onPointerUp"] = (event) => {
    if (drag.current?.pointerId !== event.pointerId) {
      return;
    }

    finishResize();

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onKeyDown: AdminAgentPanelResize["onKeyDown"] = (event) => {
    const step = ADMIN_AGENT_PANEL_RESIZE_STEP * (event.shiftKey ? 3 : 1);
    let nextWidth: number;

    switch (event.key) {
      case "ArrowLeft":
        nextWidth = width + step;
        break;
      case "ArrowRight":
        nextWidth = width - step;
        break;
      case "Home":
        nextWidth = minimumWidth;
        break;
      case "End":
        nextWidth = maximumWidth;
        break;
      default:
        return;
    }

    event.preventDefault();
    setSavedWidth(clampAdminAgentPanelWidth(nextWidth, viewportWidth));
  };

  return {
    width,
    minimumWidth,
    maximumWidth,
    isResizing: dragWidth !== undefined,
    finishResize,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onKeyDown,
  };
};
